/**
 * @file DivertOrderFlow.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-06
 * @modified 2026-10-06
 * @description Divert, start to finish, from wherever it is started — Search
 *              Customer Orders, the order window, Search Purchase Orders and
 *              the PO window all use this, so they behave the same:
 *
 *                1. the Divert window (DivertOrderModal);
 *                2. a revised copy offered for each PO a supplier or haulier
 *                   already holds whose copy names the customer — Direct
 *                   Delivery / medicated-feed PO-Ds and the PO-H (the server
 *                   decides which: orderDivertService). A PO we collect is
 *                   never offered one: its supplier sees no customer details;
 *                3. the outcome.
 */

import React, { useState } from 'react';
import DivertOrderModal from './DivertOrderModal';
import ConfirmDialog from './ConfirmDialog';
import AlertDialog from './AlertDialog';
import SendToSupplierFlow from './SendToSupplierFlow';
import { revisedPosFrom } from '../utils/poRevisions';

/**
 * @param {number}   orderId  The customer order to divert (a PO's hub order).
 * @param {object}   user     For SendToSupplierFlow's "send as".
 * @param {function} onClose  Always called when the flow ends.
 * @param {function} onDone   (result) after a divert that went through.
 */
function DivertOrderFlow({ orderId, user, onClose, onDone }) {
  const [result, setResult] = useState(null);
  const [resend, setResend] = useState(null);

  const finish = () => {
    if (result && onDone) onDone(result);
    onClose && onClose();
  };

  const pos = result ? revisedPosFrom(result) : [];
  const who = (p) => `${p.purchase_order_number} (${p.supplier_name || (p.po_type === 'haulage' ? 'haulier' : 'supplier')})`;

  return (
    <>
      <DivertOrderModal isOpen={!result} orderId={orderId} onClose={onClose} onDiverted={setResult} />

      {result && pos.length > 0 && !resend && (
        <ConfirmDialog
          isOpen
          type="info"
          title="Send the revised purchase order?"
          message={`${result.message}. ${pos.map(who).join(', ')} had already been sent with the old delivery details. `
            + `Send the revised ${pos.length === 1 ? 'version' : 'versions'}?`}
          confirmText="Send now"
          cancelText="Not now"
          onConfirm={() => setResend(pos)}
          onCancel={finish}
        />
      )}
      {resend && (
        <SendToSupplierFlow
          linkedPos={resend}
          orderNumber={result.order_number}
          user={user}
          onClose={finish}
        />
      )}
      {result && pos.length === 0 && (
        <AlertDialog isOpen type="success" title="Order diverted" message={`${result.message}.`} onClose={finish} />
      )}
    </>
  );
}

export default DivertOrderFlow;
